import React, { useMemo, useState, useEffect, useCallback, useRef } from "react"; 
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Spinner, Row, Col } from "react-bootstrap";
import { Input, Empty, Select } from "antd";
import { toast } from "react-toastify";

import useHealthChecks from "hooks/health-checks/useHealthChecks";
import useDebounce from "hooks/route-rules/useDebounce";
import paths from "../../../routes/paths";

import HealthChecksTable from "./HeathChecksTable"
import HealthCheckDrawer from "./HealthCheckDrawer";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import IconButton from "components/common/IconButton";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";

const HealthChecks = () => {
    const POLL_INTERVAL_MS = 15000;
    const navigate = useNavigate();
    const { checkId } = useParams();
    const location = useLocation();

    // Logic State
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("ALL");
    const [activeTab, setActiveTab] = useState("ALL");
    const [selectedEnabled, setSelectedEnabled] = useState("ALL");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [checkToDelete, setCheckToDelete] = useState(null);
    const [drawerHealthCheck, setDrawerHealthCheck] = useState(null);
    const isMutatingRef = useRef(false);

    const {
        healthChecks= [],
        stats,
        loading,
        pagination,
        fetchAllHealthChecks,
        searchHealthChecks,
        fetchHealthCheckById,
        updateHealthCheck,
        addHealthCheck,
        deleteHealthCheck,
        runProbe,
    } = useHealthChecks(page, pageSize);

    // URL Logic
    const isCreating = location.pathname.endsWith('/add');
    const isEditing = !!checkId && !isCreating;
    const isDrawerOpen = isCreating || isEditing;

    const handleOpenAddDrawer = () => navigate(paths.healthCheckAdd);
    const handleOpenEditDrawer = (input) => {
      const id = typeof input === "object" ? input?.id : input;
      if (!id) {
        toast.error("Health check ID is missing.");
        return;
      }
      if (typeof input === "object") setDrawerHealthCheck(input);
      navigate(paths.healthCheckDetail(id));
    };
    const handleCloseDrawer = () => navigate(paths.healthChecks);

    const selectedHealthCheckFromList = healthChecks.find((c) => String(c.id) === String(checkId));
    const selectedHealthCheck = selectedHealthCheckFromList || (drawerHealthCheck && String(drawerHealthCheck.id) === String(checkId) ? drawerHealthCheck : null);

    const statusOptions = useMemo(() => [
        { label: "🌍 ALL STATUS", value: "ALL" },
        { label: "ℹ️ INFO", value: "INFO" },
        { label: "⚠️ ALERT", value: "ALERT" },
        { label: "🟠 MEDIUM", value: "MEDIUM" },
        { label: "🔥 CRITICAL", value: "CRITICAL" },
    ], []);

    const enabledOptions = useMemo(() => [
        { label: "🌍 ALL ENABLED", value: "ALL" },
        { label: "✅ ENABLED", value: "ENABLED" },
        { label: "🚫 DISABLED", value: "DISABLED" },
    ], []);

    // Stats are computed from full dataset inside the hook.

    // Search/Refetch Logic
    const debouncedSearchTerm = useDebounce(searchTerm, 300);
    const refreshCurrentView = useCallback(() => {
        if (isMutatingRef.current) return;
        const trimmed = debouncedSearchTerm.trim();
        if (trimmed.length === 0 && selectedStatus === "ALL" && selectedEnabled === "ALL") {
            fetchAllHealthChecks(page);
            return;
        }
        searchHealthChecks(trimmed, selectedStatus, "ALL", selectedEnabled, page);
    }, [debouncedSearchTerm, selectedStatus, selectedEnabled, page, searchHealthChecks, fetchAllHealthChecks]);

    useEffect(() => { refreshCurrentView(); }, [refreshCurrentView]);

    useEffect(() => {
        const intervalId = setInterval(() => { refreshCurrentView(); }, POLL_INTERVAL_MS);
        return () => clearInterval(intervalId);
    }, [refreshCurrentView]);

    // Action Handlers
    const handleToggleActive = async (check) => {
        try {
            isMutatingRef.current = true;
            const currentlyEnabled = typeof check.active === "boolean" ? check.active : !!check.isEnabled;
            await updateHealthCheck(check.id, { isEnabled: !currentlyEnabled });
            setTimeout(() => { isMutatingRef.current = false; refreshCurrentView(); }, 350);
        } catch (err) {
            isMutatingRef.current = false;
            toast.error("Failed to toggle status");
        }
    };

    const handleDeleteConfirm = async () => {
        setIsSubmitting(true);
        try {
            await deleteHealthCheck(checkToDelete);
            setShowDeleteModal(false);
            refreshCurrentView();
        } finally { setIsSubmitting(false); }
    };

    const handleSaveHealthCheck = async (data) => {
      setIsSubmitting(true);
      try {
        await (isCreating ? addHealthCheck(data) : updateHealthCheck(checkId, data));
        navigate(paths.healthChecks);
      } finally { setIsSubmitting(false); }
    };

    return (
    <>
      <WidgetSectionTitle
        icon="briefcase-medical"
        title="Health Checks"
        subtitle="View and edit health check configurations."
        transform="shrink-2"
        className="mb-3 mt-3"
      />

      {/* ================= STATS + ADD BUTTON (Users Styling) ================= */}
      <div
        className="position-relative p-3 rounded-3 mb-4 d-flex align-items-center justify-content-between flex-wrap"
        style={{
          background: "#f7f5f5",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        }}
      >
        <div className="d-flex flex-nowrap gap-2 overflow-auto">
          {[
            { label: "HEALTHY", count: stats.healthy, border: "#198754", text: "#198754" },
            { label: "ALERT", count: stats.alert, border: "#ffc107", text: "#b08900" },
            { label: "MEDIUM", count: stats.medium, border: "#fd7e14", text: "#fd7e14" },
            { label: "CRITICAL", count: stats.critical, border: "#dc3545", text: "#dc3545" },
            { label: "DISABLED", count: stats.disabled, border: "#6c757d", text: "#6c757d" },
          ].map((item) => (
            <div
              key={item.label}
              className="p-2 rounded-3 d-flex flex-column align-items-center justify-content-center"
              style={{
                minWidth: 80,
                maxWidth: 100,
                backgroundColor: "#fff",
                borderLeft: `4px solid ${item.border}`,
              }}
            >
              <div style={{ fontSize: 10, fontWeight: 600, color: item.text, marginBottom: 4, textAlign: "center" }}>
                {item.label}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: item.text }}>
                {item.count}
              </div>
            </div>
          ))}
        </div>

        <div className="ms-auto mt-2 mt-sm-0">
          <IconButton
            size="sm"
            icon="plus"
            transform="shrink-3"
            onClick={handleOpenAddDrawer}
            style={{
              backgroundColor: "#198754",
              color: "#fff",
              border: "1px solid #198754",
              fontWeight: 600,
            }}
          >
            <span className="ms-1">Register Health Check</span>
          </IconButton>
        </div>
      </div>

      {/* ================= SEARCH + FILTERS (Users Styling) ================= */}
      <Row className="mb-2 g-3 align-items-center">
        <Col md={8} lg={9}>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <Input
              prefix={<span style={{ fontSize: 14 }}>🔍</span>}
              placeholder="Search health checks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              size="large"
              style={{
                borderRadius: 12,
                height: 30,
                flex: "1 1 250px",
                maxWidth: 300
              }}
            />

            <Select
              size="large"
              value={selectedStatus}
              onChange={(value) => { setSelectedStatus(value); setPage(0); }}
              options={statusOptions}
              style={{ minWidth: 160, height: 28 }}
            />

            <Select
              size="large"
              value={selectedEnabled}
              onChange={(value) => { setSelectedEnabled(value); setPage(0); }}
              options={enabledOptions}
              style={{ minWidth: 160, height: 28 }}
            />
          </div>
        </Col>

        <Col md={4} lg={3} className="text-end">
          <div style={{ fontSize: 13, color: "#64748b" }}>
            Results: <span className="fw-semibold text-dark">{healthChecks.length}</span> of{" "}
            <span className="fw-semibold text-dark">{pagination?.totalElements || 0}</span>
          </div>
        </Col>
      </Row>

  <div className="position-relative">
        {loading && (
          <div 
            className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center" 
            style={{ 
                zIndex: 10, 
                backgroundColor: healthChecks.length > 0 ? 'rgba(255, 255, 255, 0.6)' : '#fff',
                minHeight: 400 
            }}
          >
            <div className="text-center">
                <Spinner animation="border" variant="success" size="sm" />
                <div className="mt-2 text-500 fs-10 fw-medium">Syncing health data...</div>
            </div>
          </div>
        )}

        {healthChecks.length > 0 ? (
            <HealthChecksTable
                healthChecks={healthChecks}
                onEdit={(check) => handleOpenEditDrawer(check)}
                onToggleActive={handleToggleActive}
                setShowDeleteModal={setShowDeleteModal}
                setHealthCheckToDelete={setCheckToDelete}
                page={page}
                pageSize={pageSize}
                totalPages={pagination?.totalPages || 0}
                totalElements={pagination?.totalElements || 0}
                setPage={setPage}
                setPageSize={setPageSize}
                loading={loading}
            />
        ) : !loading && (
            <div className="py-5 bg-white rounded-3 border border-200 text-center">
                <Empty description="No health checks found" />
            </div>
        )}
      </div>

      {isDrawerOpen && (
        <HealthCheckDrawer
          show={isDrawerOpen}
          healthCheck={selectedHealthCheck}
          onClose={handleCloseDrawer}
          onSave={handleSaveHealthCheck}
          onProbe={runProbe}
          isSubmitting={isSubmitting}
        />
      )}

      <ConfirmDeleteModal
        itemName="health check"
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onConfirm={handleDeleteConfirm}
        loading={isSubmitting}
      />
    </>
  );
};

export default HealthChecks;
